import { supabase } from '../utils/supabase';

/**
 * Admin Analytics Service
 * Handles platform metrics, admin logs, and moderation
 */
class AdminAnalyticsService {
  /**
   * Get platform metrics for dashboard
   * @param {number} days - Number of days to fetch (default: 30)
   * @returns {Promise<Object>} Result with metrics
   */
  async getPlatformMetrics(days = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error} = await supabase
        .from('platform_metrics')
        .select('*')
        .gte('metric_date', startDate.toISOString().split('T')[0])
        .order('metric_date', { ascending: false });

      if (error) {
        console.error('Error fetching platform metrics:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getPlatformMetrics:', error);
      return { success: false, error: 'Failed to fetch platform metrics' };
    }
  }

  /**
   * Get current platform statistics (real-time counts)
   * @returns {Promise<Object>} Result with current stats
   */
  async getCurrentStats() {
    try {
      // Get counts from all tables in parallel
      const [usersRes, vendorsRes, bookingsRes, inquiriesRes, reviewsRes] = await Promise.all([
        supabase.from('user_profiles').select('id', { count: 'exact', head: true }),
        supabase.from('vendor_profiles').select('id', { count: 'exact', head: true }),
        supabase.from('bookings').select('id, total_amount, status', { count: 'exact' }),
        supabase.from('inquiries').select('id, status', { count: 'exact' }),
        supabase.from('reviews').select('id', { count: 'exact', head: true })
      ]);

      // Calculate revenue from completed bookings
      const completedBookings = bookingsRes.data?.filter(b => b.status === 'completed') || [];
      const totalRevenue = completedBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);

      // Count pending inquiries
      const pendingInquiries = inquiriesRes.data?.filter(i => i.status === 'pending').length || 0;

      return {
        success: true,
        data: {
          totalUsers: usersRes.count || 0,
          activeVendors: vendorsRes.count || 0,
          totalBookings: bookingsRes.count || 0,
          completedBookings: completedBookings.length,
          totalRevenue,
          totalInquiries: inquiriesRes.count || 0,
          pendingInquiries,
          totalReviews: reviewsRes.count || 0
        }
      };
    } catch (error) {
      console.error('Error in getCurrentStats:', error);
      return { success: false, error: 'Failed to fetch current stats' };
    }
  }

  /**
   * Get vendor approval queue (pending vendors)
   * @returns {Promise<Object>} Result with pending vendors
   */
  async getVendorApprovalQueue() {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles')
        .select(`
          *,
          user:user_profiles!user_id(id, full_name, email, phone)
        `)
        .eq('is_verified', false)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching vendor approval queue:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getVendorApprovalQueue:', error);
      return { success: false, error: 'Failed to fetch vendor approval queue' };
    }
  }

  /**
   * Approve vendor
   * @param {string} vendorId - Vendor UUID
   * @param {string} adminId - Admin user UUID
   * @returns {Promise<Object>} Result
   */
  async approveVendor(vendorId, adminId) {
    try {
      // Update vendor
      const { data, error } = await supabase
        .from('vendor_profiles')
        .update({
          is_verified: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', vendorId)
        .select()
        .single();

      if (error) {
        console.error('Error approving vendor:', error);
        return { success: false, error: error.message };
      }

      // Log admin action
      await this.logAdminAction(adminId, 'approve_vendor', 'vendor', vendorId, {
        vendor_name: data.business_name
      });

      return { success: true, data };
    } catch (error) {
      console.error('Error in approveVendor:', error);
      return { success: false, error: 'Failed to approve vendor' };
    }
  }

  /**
   * Reject vendor
   * @param {string} vendorId - Vendor UUID
   * @param {string} adminId - Admin user UUID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} Result
   */
  async rejectVendor(vendorId, adminId, reason) {
    try {
      // For now, we'll just mark as not verified and log it
      // In production, you might want to add a rejection reason field
      
      // Log admin action
      await this.logAdminAction(adminId, 'reject_vendor', 'vendor', vendorId, {
        reason
      });

      return { success: true };
    } catch (error) {
      console.error('Error in rejectVendor:', error);
      return { success: false, error: 'Failed to reject vendor' };
    }
  }

  /**
   * Get moderation queue
   * @param {string} status - Filter by status (pending, approved, rejected)
   * @returns {Promise<Object>} Result with moderation items
   */
  async getModerationQueue(status = 'pending') {
    try {
      const { data, error } = await supabase
        .from('moderation_queue')
        .select(`
          *,
          reporter:user_profiles!reported_by(id, full_name, email),
          moderator:user_profiles!moderator_id(id, full_name)
        `)
        .eq('status', status)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching moderation queue:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getModerationQueue:', error);
      return { success: false, error:'Failed to fetch moderation queue' };
    }
  }

  /**
   * Moderate content
   * @param {string} itemId - Moderation item UUID
   * @param {string} moderatorId - Moderator UUID
   * @param {string} action - Action (approve, reject, escalate)
   * @param {string} notes - Moderator notes
   * @returns {Promise<Object>} Result
   */
  async moderateContent(itemId, moderatorId, action, notes = '') {
    try {
      const status = action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'escalated';

      const { data, error } = await supabase
        .from('moderation_queue')
        .update({
          status,
          moderator_id: moderatorId,
          moderator_notes: notes,
          resolved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', itemId)
        .select()
        .single();

      if (error) {
        console.error('Error moderating content:', error);
        return { success: false, error: error.message };
      }

      // Log admin action
      await this.logAdminAction(moderatorId, `moderate_${action}`, data.resource_type, data.resource_id, {
        moderation_id: itemId,
        notes
      });

      return { success: true, data };
    } catch (error) {
      console.error('Error in moderateContent:', error);
      return { success: false, error: 'Failed to moderate content' };
    }
  }

  /**
   * Get admin activity logs
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Result with logs
   */
  async getAdminLogs(filters = {}) {
    try {
      let query = supabase
        .from('admin_logs')
        .select(`
          *,
          admin:user_profiles!admin_id(id, full_name, email)
        `)
        .order('created_at', { ascending: false });

      if (filters.adminId) {
        query = query.eq('admin_id', filters.adminId);
      }

      if (filters.action) {
        query = query.eq('action', filters.action);
      }

      if (filters.resourceType) {
        query = query.eq('resource_type', filters.resourceType);
      }

      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }

      if (filters.endDate) {
        query = query.lte('created_at', filters.endDate);
      }

      const limit = filters.limit || 100;
      query = query.limit(limit);

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching admin logs:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getAdminLogs:', error);
      return { success: false, error: 'Failed to fetch admin logs' };
    }
  }

  /**
   * Log admin action
   * @param {string} adminId - Admin UUID
   * @param {string} action - Action performed
   * @param {string} resourceType - Type of resource
   * @param {string} resourceId - Resource UUID
   * @param {Object} details - Additional details
   * @returns {Promise<Object>} Result
   */
  async logAdminAction(adminId, action, resourceType, resourceId, details = {}) {
    try {
      const { data, error } = await supabase
        .from('admin_logs')
        .insert([{
          admin_id: adminId,
          action,
          resource_type: resourceType,
          resource_id: resourceId,
          details
        }])
        .select()
        .single();

      if (error) {
        console.error('Error logging admin action:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in logAdminAction:', error);
      return { success: false, error: 'Failed to log admin action' };
    }
  }

  /**
   * Get user management data
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Result with users
   */
  async getUsers(filters = {}) {
    try {
      let query = supabase
        .from('user_profiles')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      if (filters.role) {
        query = query.eq('role', filters.role);
      }

      if (filters.accountStatus) {
        query = query.eq('account_status', filters.accountStatus);
      }

      if (filters.search) {
        query = query.or(`full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
      }

      const page = filters.page || 1;
      const limit = filters.limit || 50;
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching users:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: data || [],
        count,
        page,
        totalPages: Math.ceil(count / limit)
      };
    } catch (error) {
      console.error('Error in getUsers:', error);
      return { success: false, error: 'Failed to fetch users' };
    }
  }

  /**
   * Update user status
   * @param {string} userId - User UUID
   * @param {string} status - New status
   * @param {string} adminId - Admin UUID
   * @returns {Promise<Object>} Result
   */
  async updateUserStatus(userId, status, adminId) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          account_status: status,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        console.error('Error updating user status:', error);
        return { success: false, error: error.message };
      }

      // Log admin action
      await this.logAdminAction(adminId, 'update_user_status', 'user', userId, {
        new_status: status
      });

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateUserStatus:', error);
      return { success: false, error: 'Failed to update user status' };
    }
  }

  /**
   * Get system health metrics
   * @param {number} hours - Number of hours to fetch (default: 24)
   * @returns {Promise<Object>} Result with health metrics
   */
  async getSystemHealth(hours = 24) {
    try {
      const startTime = new Date();
      startTime.setHours(startTime.getHours() - hours);

      const { data, error } = await supabase
        .from('system_health')
        .select('*')
        .gte('timestamp', startTime.toISOString())
        .order('timestamp', { ascending: false });

      if (error) {
        console.error('Error fetching system health:', error);
        return { success: false, error: error.message };
      }

      // Calculate averages
      if (data && data.length > 0) {
        const avgDbResponse = data.reduce((sum, m) => sum + (m.database_response_time || 0), 0) / data.length;
        const avgApiResponse = data.reduce((sum, m) => sum + (m.api_response_time || 0), 0) / data.length;
        const avgUptime = data.reduce((sum, m) => sum + (m.uptime_percentage || 100), 0) / data.length;

        return {
          success: true,
          data,
          summary: {
            avgDatabaseResponseTime: Math.round(avgDbResponse * 10) / 10,
            avgApiResponseTime: Math.round(avgApiResponse * 10) / 10,
            avgUptime: Math.round(avgUptime * 100) / 100,
            totalErrors: data.reduce((sum, m) => sum + (m.error_count || 0), 0)
          }
        };
      }

      return { success: true, data: [], summary: null };
    } catch (error) {
      console.error('Error in getSystemHealth:', error);
      return { success: false, error: 'Failed to fetch system health' };
    }
  }

  /**
   * Record daily platform metrics (to be run daily via cron)
   * @returns {Promise<Object>} Result
   */
  async recordDailyMetrics() {
    try {
      const stats = await this.getCurrentStats();
      
      if (!stats.success) {
        return stats;
      }

      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('platform_metrics')
        .upsert([{
          metric_date: today,
          total_users: stats.data.totalUsers,
          active_vendors: stats.data.activeVendors,
          total_bookings: stats.data.totalBookings,
          completed_bookings: stats.data.completedBookings,
          total_revenue: stats.data.totalRevenue,
          total_inquiries: stats.data.totalInquiries,
          total_reviews: stats.data.totalReviews
        }], {
          onConflict: 'metric_date'
        })
        .select()
        .single();

      if (error) {
        console.error('Error recording daily metrics:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in recordDailyMetrics:', error);
      return { success: false, error: 'Failed to record daily metrics' };
    }
  }
}

// Export singleton instance
const adminAnalyticsService = new AdminAnalyticsService();
export default adminAnalyticsService;
